import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  Activity,
  CheckCircle2,
  Clock,
  Loader2,
  XCircle,
} from "lucide-react";
import {
  PROPAGATION_STATUS,
  type PropagationStoreStatus,
} from "@/utils/campaign-types";

type LogEntry = {
  id: number;
  timestamp: string;
  status: PropagationStoreStatus;
  attemptNumber: number;
  errorMessage?: string | null;
  duration?: number; // em segundos
};

type StoreLogsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storeCode: number;
  storeName: string;
  logs: LogEntry[];
};

const statusConfig: Record<
  PropagationStoreStatus,
  { icon: typeof Clock; color: string; dot: string; label: string }
> = {
  [PROPAGATION_STATUS.PENDING]: {
    icon: Clock,
    color: "text-amber-600 dark:text-amber-400",
    dot: "bg-amber-500",
    label: "Pendente",
  },
  [PROPAGATION_STATUS.IN_PROGRESS]: {
    icon: Loader2,
    color: "text-blue-600 dark:text-blue-400",
    dot: "bg-blue-500",
    label: "Em andamento",
  },
  [PROPAGATION_STATUS.SUCCESS]: {
    icon: CheckCircle2,
    color: "text-green-600 dark:text-green-400",
    dot: "bg-green-500",
    label: "Sucesso",
  },
  [PROPAGATION_STATUS.FAILED]: {
    icon: XCircle,
    color: "text-red-600 dark:text-red-400",
    dot: "bg-red-500",
    label: "Falha",
  },
};

const formatDateTime = (value: string): string =>
  new Date(value).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export function StoreLogsDialog({
  open,
  onOpenChange,
  storeCode,
  storeName,
  logs,
}: StoreLogsDialogProps) {
  const successCount = logs.filter(
    (l) => l.status === PROPAGATION_STATUS.SUCCESS
  ).length;
  const failCount = logs.filter(
    (l) => l.status === PROPAGATION_STATUS.FAILED
  ).length;
  const current = logs[0];
  const currentConfig = current ? statusConfig[current.status] : null;
  const CurrentIcon = currentConfig?.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            Histórico de Sincronização
          </DialogTitle>
          <DialogDescription>
            Loja #{storeCode}
            {storeName ? ` - ${storeName}` : ""}
          </DialogDescription>
        </DialogHeader>

        {/* Resumo */}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border bg-muted/30 p-3">
          <div className="flex items-center gap-2">
            {currentConfig && CurrentIcon ? (
              <>
                <CurrentIcon
                  className={cn(
                    "h-4 w-4",
                    currentConfig.color,
                    current?.status === PROPAGATION_STATUS.IN_PROGRESS &&
                      "animate-spin"
                  )}
                />
                <span className="text-sm font-medium">
                  {currentConfig.label}
                </span>
              </>
            ) : (
              <span className="text-sm text-muted-foreground">
                Sem registros
              </span>
            )}
          </div>
          <div className="text-sm text-muted-foreground">
            {logs.length} registro(s) · {successCount} sucesso(s) · {failCount}{" "}
            falha(s)
          </div>
        </div>

        {/* Linha do tempo */}
        <ScrollArea className="max-h-[420px] pr-3">
          {logs.length > 0 ? (
            <ol className="relative ml-2 space-y-5 border-l pl-5">
              {logs.map((log, index) => {
                const config = statusConfig[log.status];
                const Icon = config.icon;
                const isLatest = index === 0;

                return (
                  <li key={log.id} className="relative">
                    {/* Marcador na linha do tempo */}
                    <span
                      className={cn(
                        "absolute -left-[27px] top-1 h-3 w-3 rounded-full ring-4 ring-background",
                        config.dot
                      )}
                    />
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Icon
                          className={cn(
                            "h-4 w-4",
                            config.color,
                            log.status === PROPAGATION_STATUS.IN_PROGRESS &&
                              "animate-spin"
                          )}
                        />
                        <span className="text-sm font-medium">
                          {config.label}
                        </span>
                        {log.attemptNumber > 0 && (
                          <span className="text-xs text-muted-foreground">
                            Tentativa {log.attemptNumber}
                          </span>
                        )}
                        {isLatest && (
                          <Badge variant="secondary" className="text-[10px]">
                            Mais recente
                          </Badge>
                        )}
                      </div>
                      <time className="text-xs text-muted-foreground">
                        {formatDateTime(log.timestamp)}
                      </time>
                    </div>

                    {log.status === PROPAGATION_STATUS.FAILED &&
                      log.errorMessage && (
                        <p className="mt-2 break-words rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
                          {log.errorMessage}
                        </p>
                      )}
                  </li>
                );
              })}
            </ol>
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-muted-foreground">
              <Activity className="h-10 w-10 opacity-20" />
              <p className="text-sm">Nenhum registro para esta loja</p>
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
